import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Upload, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminListProducts, createProduct, updateProduct, uploadFile } from "@/lib/adminApi";
import { getCategories } from "@/lib/api";

const EMPTY = {
  name: "", category: "", code: "", category_name: "", tagline: "", short_description: "",
  description: "", composition: "", benefits: [], technical_highlights: [], dosage: "",
  application: "", storage: "", presentation: "", image: "", gallery: [], brochure_url: "",
  seo_title: "", seo_description: "", status: "published", featured: false, order: 0, related: [],
};

function Field({ label, children, hint }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      {children}
      {hint && <p className="text-xs mt-1" style={{ color: "#94a3b8" }}>{hint}</p>}
    </div>
  );
}

export default function ProductForm() {
  const { slug } = useParams();
  const isNew = !slug || slug === "new";
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
    if (!isNew) {
      adminListProducts()
        .then((all) => {
          const p = all.find((x) => x.slug === slug);
          if (p) setForm({ ...EMPTY, ...p });
          else toast.error("Product not found");
        })
        .catch(() => toast.error("Failed to load"))
        .finally(() => setLoading(false));
    }
  }, [slug, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onCategory = (code) => {
    const cat = categories.find((c) => c.code === code);
    set("category", code);
    if (cat && !form.category_name) set("category_name", cat.name);
  };

  const doUpload = async (file, target, gallery = false) => {
    if (!file) return;
    setUploading(target);
    try {
      const res = await uploadFile(file);
      const url = res.url;
      if (gallery) set("gallery", [...form.gallery, url]);
      else set(target, url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading("");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.category) {
      toast.error("Name and category are required");
      return;
    }
    setSaving(true);
    const payload = { ...form, order: Number(form.order) || 0 };
    try {
      if (isNew) await createProduct(payload);
      else await updateProduct(slug, payload);
      toast.success(isNew ? "Product created" : "Product updated");
      nav("/admin/products");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin" style={{ color: "#087FBF" }} /></div>;

  return (
    <form onSubmit={save} className="max-w-3xl">
      <button type="button" onClick={() => nav("/admin/products")} className="flex items-center gap-1.5 text-sm font-semibold mb-4" style={{ color: "#087FBF" }}>
        <ArrowLeft className="w-4 h-4" /> Back to Products
      </button>
      <h1 className="font-heading text-3xl font-extrabold" style={{ color: "#063B63" }}>{isNew ? "Add Product" : `Edit ${form.name}`}</h1>

      <div className="mt-6 space-y-5 biosyn-card p-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Product Name *"><Input data-testid="pf-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" /></Field>
          <Field label="Product Code / SKU"><Input data-testid="pf-code" value={form.code} onChange={(e) => set("code", e.target.value)} className="mt-1.5" /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Category *">
            <Select value={form.category} onValueChange={onCategory}>
              <SelectTrigger data-testid="pf-category" className="mt-1.5"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Category Display Name"><Input data-testid="pf-catname" value={form.category_name} onChange={(e) => set("category_name", e.target.value)} className="mt-1.5" /></Field>
        </div>
        <Field label="Tagline"><Input data-testid="pf-tagline" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} className="mt-1.5" /></Field>
        <Field label="Short Description"><Textarea data-testid="pf-short" value={form.short_description} onChange={(e) => set("short_description", e.target.value)} rows={2} className="mt-1.5" /></Field>
        <Field label="Full Description"><Textarea data-testid="pf-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="mt-1.5" /></Field>

        {/* Images */}
        <Field label="Primary Product Image" hint="Upload packaging photo (PNG/JPG/WebP).">
          <div className="mt-1.5 flex items-center gap-4">
            <div className="w-24 h-24 rounded-xl border flex items-center justify-center overflow-hidden" style={{ borderColor: "rgba(8,127,191,0.15)", background: "#F5FAFC" }}>
              {form.image ? <img src={form.image} alt="preview" className="max-w-full max-h-full object-contain" /> : <span className="text-xs" style={{ color: "#94a3b8" }}>No image</span>}
            </div>
            <label className="btn-outline-blue !py-2 cursor-pointer text-sm">
              {uploading === "image" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
              <input type="file" accept="image/*" className="hidden" data-testid="pf-image-upload" onChange={(e) => doUpload(e.target.files[0], "image")} />
            </label>
            {form.image && <button type="button" onClick={() => set("image", "")} className="text-sm" style={{ color: "#c0392b" }}>Remove</button>}
          </div>
        </Field>

        <Field label="Gallery Images" hint="Upload multiple additional images.">
          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            {form.gallery.map((g, i) => (
              <div key={i} className="relative w-20 h-20 rounded-lg border overflow-hidden flex items-center justify-center" style={{ borderColor: "rgba(8,127,191,0.15)", background: "#F5FAFC" }}>
                <img src={g} alt={`gallery ${i}`} className="max-w-full max-h-full object-contain" />
                <button type="button" onClick={() => set("gallery", form.gallery.filter((_, idx) => idx !== i))} className="absolute top-0.5 right-0.5 bg-white rounded-full p-0.5 shadow"><X className="w-3 h-3" style={{ color: "#c0392b" }} /></button>
              </div>
            ))}
            <label className="btn-outline-blue !py-2 cursor-pointer text-sm">
              {uploading === "gallery" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Add
              <input type="file" accept="image/*" className="hidden" data-testid="pf-gallery-upload" onChange={(e) => doUpload(e.target.files[0], "gallery", true)} />
            </label>
          </div>
        </Field>

        {/* Brochure */}
        <Field label="Product Brochure (PDF)" hint="If uploaded, a Download Brochure button appears on the product page.">
          <div className="mt-1.5 flex items-center gap-4">
            {form.brochure_url ? (
              <a href={form.brochure_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#087FBF" }}><FileText className="w-4 h-4" /> View PDF</a>
            ) : <span className="text-sm" style={{ color: "#94a3b8" }}>No brochure</span>}
            <label className="btn-outline-blue !py-2 cursor-pointer text-sm">
              {uploading === "brochure_url" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload PDF
              <input type="file" accept="application/pdf" className="hidden" data-testid="pf-brochure-upload" onChange={(e) => doUpload(e.target.files[0], "brochure_url")} />
            </label>
            {form.brochure_url && <button type="button" onClick={() => set("brochure_url", "")} className="text-sm" style={{ color: "#c0392b" }}>Remove</button>}
          </div>
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Benefits" hint="One per line">
            <Textarea data-testid="pf-benefits" value={form.benefits.join("\n")} onChange={(e) => set("benefits", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} rows={5} className="mt-1.5" />
          </Field>
          <Field label="Technical Highlights" hint="One per line">
            <Textarea data-testid="pf-highlights" value={form.technical_highlights.join("\n")} onChange={(e) => set("technical_highlights", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} rows={5} className="mt-1.5" />
          </Field>
        </div>
        <Field label="Composition"><Textarea value={form.composition} onChange={(e) => set("composition", e.target.value)} rows={2} className="mt-1.5" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Dosage"><Textarea value={form.dosage} onChange={(e) => set("dosage", e.target.value)} rows={2} className="mt-1.5" /></Field>
          <Field label="Presentation / Pack Size"><Input value={form.presentation} onChange={(e) => set("presentation", e.target.value)} className="mt-1.5" /></Field>
        </div>
        <Field label="Application"><Textarea value={form.application} onChange={(e) => set("application", e.target.value)} rows={2} className="mt-1.5" /></Field>
        <Field label="Storage"><Textarea value={form.storage} onChange={(e) => set("storage", e.target.value)} rows={2} className="mt-1.5" /></Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="SEO Title"><Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className="mt-1.5" /></Field>
          <Field label="Display Order"><Input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className="mt-1.5" /></Field>
        </div>
        <Field label="SEO Description"><Textarea value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} rows={2} className="mt-1.5" /></Field>
        <Field label="Related Products" hint="Comma-separated product slugs (e.g. biosyn-min, biosyn-gut)">
          <Input value={form.related.join(", ")} onChange={(e) => set("related", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className="mt-1.5" />
        </Field>

        <div className="flex items-center gap-8 pt-2">
          <label className="flex items-center gap-3 cursor-pointer">
            <Switch checked={form.status === "published"} onCheckedChange={(v) => set("status", v ? "published" : "draft")} data-testid="pf-status" />
            <span className="text-sm font-semibold" style={{ color: "#063B63" }}>Published</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="pf-featured" />
            <span className="text-sm font-semibold" style={{ color: "#063B63" }}>Featured</span>
          </label>
        </div>
      </div>

      <div className="mt-5 flex gap-3">
        <button type="submit" disabled={saving} data-testid="pf-save" className="btn-primary disabled:opacity-70">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {isNew ? "Create Product" : "Save Changes"}
        </button>
        <button type="button" onClick={() => nav("/admin/products")} className="btn-outline-blue">Cancel</button>
      </div>
    </form>
  );
}
